import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import { imageUrl } from "../../../api/apiConfig/apiConfig";
import {
  canAccessTraderTab,
  getDefaultDashboardRoute,
  getVisibleTraderTabSections,
  hasPermission,
  isKycOnlyAdmin,
  PERMISSION_KEYS,
} from "../../../utils/permissions";
import "../Home/Homepage.css";
import "./TraderDetailsPage.css";
import InternalTransferSettingsPanel from "./InternalTransferSettingsPanel";
import { STAKING_WALLET_HINT, formatWalletType, isStakingWallet } from "../../../utils/walletTypes";

const KYC_LABELS = { 0: "Not Verified", 1: "Pending", 2: "Verified", 3: "Verified" };
const STATUS_LABELS = { 0: "Inactive", 1: "Active" };

/** Normalize API `account_status` (e.g. "active", "Active", "blocked"). */
const normAccountStatus = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");

const isAccountStatusBlocked = (v) => normAccountStatus(v) === "blocked";

const isAccountStatusActive = (v) => normAccountStatus(v) === "active";

const formatDate = (iso) => {
  if (iso == null || iso === "") return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

const formatNum = (n) => (n != null && !Number.isNaN(Number(n)) ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 10 }) : "—");

const extractDecimal = (val) => {
  if (val == null) return null;
  if (typeof val === "object" && "$numberDecimal" in val) return val.$numberDecimal;
  return val;
};

const TRADER_TAB_SECTIONS = [
  {
    label: "Profile",
    tabs: [
      { key: "user", label: "User Details", icon: "fa-user" },
      { key: "kyc", label: "KYC Details", icon: "fa-id-card" },
    ],
  },
  {
    label: "Wallet",
    tabs: [
      { key: "wallet", label: "Wallet Details", icon: "fa-wallet" },
      { key: "deposit", label: "Deposit / Withdrawal", icon: "fa-exchange-alt" },
    ],
  },
  {
    label: "Trading",
    tabs: [
      { key: "spotOpen", label: "Spot Orders", icon: "fa-list" },
      { key: "futureOpen", label: "Future  Orders", icon: "fa-chart-line" },
      { key: "futurePositions", label: "Future Open Positions", icon: "fa-bullseye" },
      { key: "futureHistory", label: "Future Trade History", icon: "fa-history" },
    ],
  },
  {
    label: "Earning",
    tabs: [
      { key: "staking", label: "Staking", icon: "fa-coins" },
      { key: "launchpad", label: "Launchpad", icon: "fa-rocket" },
    ],
  },
  {
    label: "Transfer & Swap",
    tabs: [
      { key: "internalWallet", label: "Internal Transfers", icon: "fa-wallet" },
      { key: "swapping", label: "Swapping History", icon: "fa-sync-alt" },
    ],
  },
  {
    label: "P2P",
    tabs: [
      { key: "p2p", label: "P2P Orders", icon: "fa-exchange-alt" },
      { key: "p2pAds", label: "P2P Ads", icon: "fa-ad" },
      { key: "p2pPaymentMethods", label: "P2P Payment Methods", icon: "fa-credit-card" },
    ],
  },
  {
    label: "Support & Referral",
    tabs: [
      { key: "support", label: "Support Tickets", icon: "fa-headset" },
      { key: "referral", label: "Referral", icon: "fa-gift" },
    ],
  },
];

/** Thumbnail that opens full image on click */
const KYCImageThumb = ({ src, alt, label, onZoom }) => {
  if (!src) return null;
  return (
    <div className="kyc-image-thumb" onClick={() => onZoom(src)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onZoom(src)} title="Click to zoom">
      <img src={src} alt={alt || label || "Document"} />
      {label && <span className="kyc-image-thumb__label">{label}</span>}
    </div>
  );
};

const ApproveKycModal = ({ docType, docLabel, onConfirm, onClose, loading }) => (
  <div className="kyc-modal-overlay" onClick={onClose}>
    <div className="kyc-modal" onClick={(e) => e.stopPropagation()}>
      <div className="kyc-modal__header">
        <h3 className="kyc-modal__title">Approve {docLabel}</h3>
        <button type="button" className="kyc-modal__close" onClick={onClose} aria-label="Close"><i className="fas fa-times" /></button>
      </div>
      <div className="kyc-modal__body">
        <p>Are you sure you want to approve {docLabel}? This will mark it as verified.</p>
        <div className="kyc-modal__footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={loading} onClick={() => onConfirm(docType)}>
            {loading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />} Confirm Approve
          </button>
        </div>
      </div>
    </div>
  </div>
);

const REJECT_REASONS = [
  { label: "Blur image", value: "The image is blurry. Please re-upload a clearer copy." },
  { label: "Re-upload document", value: "Please re-upload your document. The current file could not be verified." },
  { label: "Document expired", value: "This document has expired. Please upload a valid, current document." },
  { label: "Invalid document", value: "The document submitted is invalid. Please upload a valid identity document." },
  { label: "Face not matching", value: "The face in the selfie does not match the document photo." },
  { label: "Poor quality", value: "The document is unreadable due to poor quality. Please upload a clearer image." },
  { label: "Wrong document type", value: "Wrong document type was submitted. Please upload the required document." },
  { label: "Information mismatch", value: "The information on the document does not match your account details." },
];

const RejectKycDocModal = ({ docType, docLabel, onConfirm, onClose, loading }) => {
  const [rejectReason, setRejectReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!rejectReason.trim()) return;
    onConfirm(docType, rejectReason.trim(), adminNotes.trim());
  };
  return (
    <div className="kyc-modal-overlay" onClick={onClose}>
      <div className="kyc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kyc-modal__header">
          <h3 className="kyc-modal__title">Reject {docLabel}</h3>
          <button type="button" className="kyc-modal__close" onClick={onClose} aria-label="Close"><i className="fas fa-times" /></button>
        </div>
        <form onSubmit={handleSubmit} className="kyc-modal__body">
          <p>Reject {docLabel}? Reject reason is required.</p>
          <div className="kyc-modal__field">
            <label>Reject reason <span className="text-danger">*</span></label>
            <div className="kyc-reject-reasons">
              {REJECT_REASONS.map((r) => (
                <button key={r.label} type="button" className={`kyc-reject-chip ${rejectReason === r.value ? "kyc-reject-chip--active" : ""}`} onClick={() => setRejectReason(r.value)} title={r.value}>
                  {r.label}
                </button>
              ))}
            </div>
            <input type="text" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Or type a custom reason (visible to user)" className="kyc-reject-input" />
          </div>
          <div className="kyc-modal__field">
            <label>Admin notes (optional)</label>
            <input type="text" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Additional notes" />
          </div>
          <div className="kyc-modal__footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-danger" disabled={loading || !rejectReason.trim()}>Reject</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TabPagination = ({ pagination, onPageChange, onLimitChange }) => {
  if (!pagination || pagination.totalPages <= 1) return null;
  return (
    <div className="trader-tab-pagination">
      <span className="trader-tab-pagination__info">
        Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
      </span>
      <div className="trader-tab-pagination__btns">
        <button
          type="button"
          className="trader-tab-pagination__btn"
          disabled={pagination.page <= 1}
          onClick={() => onPageChange(pagination.page - 1)}
        >
          Prev
        </button>
        <button
          type="button"
          className="trader-tab-pagination__btn"
          disabled={pagination.page >= pagination.totalPages}
          onClick={() => onPageChange(pagination.page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const VALID_TABS = new Set(["user", "kyc", "wallet", "deposit", "spotOpen", "futureOpen", "futurePositions", "futureHistory", "staking", "launchpad", "internalWallet", "swapping", "p2p", "p2pAds", "p2pPaymentMethods", "support", "referral"]);

const TraderDetailsPage = () => {
  const { traderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const traderContentRef = useRef(null);
  const kycOnlyMode = isKycOnlyAdmin();
  const kycRowFromNav = location.state?.kycRow ?? null;

  const scrollTraderContentIntoViewIfMobile = () => {
    requestAnimationFrame(() => {
      if (typeof window === "undefined" || window.innerWidth > 992 || !traderContentRef.current) return;
      traderContentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const tabFromUrl = searchParams.get("tab") || "user";
  const visibleTabSections = useMemo(
    () => getVisibleTraderTabSections(TRADER_TAB_SECTIONS),
    []
  );
  const allowedTabKeys = useMemo(
    () => new Set(visibleTabSections.flatMap((section) => section.tabs.map((tab) => tab.key))),
    [visibleTabSections]
  );
  const defaultTabForAdmin = allowedTabKeys.has("kyc")
    ? "kyc"
    : allowedTabKeys.has("user")
      ? "user"
      : [...allowedTabKeys][0] || "user";
  const resolvedTab = VALID_TABS.has(tabFromUrl) && allowedTabKeys.has(tabFromUrl)
    ? tabFromUrl
    : defaultTabForAdmin;
  const activeTab = resolvedTab;

  useEffect(() => {
    if (!VALID_TABS.has(tabFromUrl) || !allowedTabKeys.has(tabFromUrl)) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (defaultTabForAdmin === "user") next.delete("tab");
        else next.set("tab", defaultTabForAdmin);
        return next;
      }, { replace: true });
    }
  }, [tabFromUrl, allowedTabKeys, defaultTabForAdmin, setSearchParams]);

  const setActiveTab = (tab) => {
    const safeTab = VALID_TABS.has(tab) && canAccessTraderTab(tab) ? tab : defaultTabForAdmin;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (safeTab === "user") next.delete("tab");
      else next.set("tab", safeTab);
      return next;
    }, { replace: true });
  };
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [tabPagination, setTabPagination] = useState({});
  const [kycZoomedImage, setKycZoomedImage] = useState(null);
  const [kycApproveModal, setKycApproveModal] = useState(null);
  const [kycRejectModal, setKycRejectModal] = useState(null);
  const [kycActionLoading, setKycActionLoading] = useState(null);
  const [depositFilter, setDepositFilter] = useState("all");
  const [p2pAdsFilter, setP2pAdsFilter] = useState("all");
  const [statusUpdating, setStatusUpdating] = useState(false);

  const profile = detail?.profile;
  const portfolio = detail?.portfolio;
  const trader = useMemo(
    () =>
      profile
        ? {
          _id: profile._id ?? traderId,
          firstName: profile.firstName ?? profile.first_name,
          lastName: profile.lastName ?? profile.last_name,
          emailId: profile.emailId ?? profile.email_masked,
          mobileNumber: profile.mobileNumber
            ? `${(profile.country_code ?? "").toString().trim()} ${profile.mobileNumber}`.trim()
            : (profile.mobile_masked ?? "—"),
          uuid: profile.uuid,
          status: profile.status,
          kycVerified: profile.kycVerified ?? profile.kyc_verified ?? 0,
          referral_code: profile.referral_code,
          account_status: profile.account_status,
          createdAt: profile.createdAt ?? profile.signup_date,
        }
        : null,
    [profile, traderId]
  );

  const fetchDetail = useCallback(async () => {
    if (!traderId) return;
    setLoading(true);
    setError("");

    if (kycOnlyMode) {
      const res = await AdminService.getUserKyc(traderId);
      if (res?.success) {
        const kyc = res?.data && !Array.isArray(res.data) ? res.data : null;
        const user = kycRowFromNav?.user || kyc?.user || {};
        const overall = String(kyc?.overall_status ?? kycRowFromNav?.overall_status ?? "").toLowerCase();
        setDetail({
          profile: {
            _id: traderId,
            first_name: kyc?.first_name ?? kycRowFromNav?.first_name ?? user.first_name,
            last_name: kyc?.last_name ?? kycRowFromNav?.last_name ?? user.last_name,
            email_masked: user.email_masked ?? user.email ?? user.emailId,
            mobile_masked: user.mobile_masked ?? user.mobileNumber ?? user.phone,
            uuid: user.uuid,
            kyc_verified: overall === "approved" || overall === "verified" ? 2 : 1,
          },
        });
        if (kyc) {
          setTabData((prev) => ({ ...prev, kyc }));
        }
      } else if (kycRowFromNav) {
        const user = kycRowFromNav.user || {};
        const overall = String(kycRowFromNav.overall_status ?? "").toLowerCase();
        setDetail({
          profile: {
            _id: traderId,
            first_name: kycRowFromNav.first_name,
            last_name: kycRowFromNav.last_name,
            email_masked: user.email_masked ?? user.email,
            mobile_masked: user.mobile_masked,
            uuid: user.uuid,
            kyc_verified: overall === "approved" || overall === "verified" ? 2 : 1,
          },
        });
      } else {
        setError(res?.message || "Failed to load KYC details.");
        setDetail(null);
      }
      setLoading(false);
      return;
    }

    const res = await AdminService.getUserDetail(traderId);
    setLoading(false);
    if (res?.success) {
      setDetail(res.data);
    } else {
      setError(res?.message || "Failed to load user details.");
      setDetail(null);
    }
  }, [traderId, kycOnlyMode, kycRowFromNav]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const fetchTab = useCallback(async (tab, page = 1) => {
    if (!traderId) return;
    setTabLoading((p) => ({ ...p, [tab]: true }));
    const limit = 20;
    const params = { page, limit };
    let res;
    switch (tab) {
      case "wallet":
        res = await AdminService.getUserWallets(traderId, params);
        break;
      case "deposit":
        if (depositFilter && depositFilter !== "all") params.transaction_type = depositFilter;
        res = await AdminService.getUserTransactions(traderId, params);
        break;
      case "kyc":
        res = await AdminService.getUserKyc(traderId);
        break;
      case "internalWallet":
        res = await AdminService.getUserInternalTransfers(traderId, params);
        break;
      case "spotHistory":
        res = await AdminService.getUserSpotTrades(traderId, params);
        break;
      case "spotOpen":
        res = await AdminService.getUserSpotOrders(traderId, params);
        break;
      case "swapping":
        res = await AdminService.getUserSwapHistory(traderId, params);
        break;
      case "staking":
        res = await AdminService.getUserEarningPlans(traderId, params);
        break;
      case "launchpad":
        res = await AdminService.getUserLaunchpad(traderId, params);
        break;
      case "support":
        res = await AdminService.getUserSupportTickets(traderId, params);
        break;
      case "referral":
        res = await AdminService.getUserReferral(traderId);
        break;
      case "p2p":
        res = await AdminService.getUserP2pOrders(traderId, params);
        break;
      case "p2pAds":
        if (p2pAdsFilter && p2pAdsFilter !== "all") params.side = p2pAdsFilter;
        res = await AdminService.getUserP2pAds(traderId, params);
        break;
      case "p2pPaymentMethods":
        res = await AdminService.getUserP2pPaymentMethods(traderId, params);
        break;
      case "futureOpen":
        res = await AdminService.getUserFuturesOrders(traderId, params);
        break;
      case "futurePositions":
        res = await AdminService.getUserFuturesPositions(traderId, params);
        break;
      case "futureHistory":
        res = await AdminService.getUserFuturesTrades(traderId, params);
        break;
      default:
        setTabLoading((p) => ({ ...p, [tab]: false }));
        return;
    }
    setTabLoading((p) => ({ ...p, [tab]: false }));
    if (res?.success) {
      setTabData((p) => ({ ...p, [tab]: res.data }));
      const pag = res.pagination;
      if (pag) {
        setTabPagination((p) => ({ ...p, [tab]: { ...pag, page: pag.page ?? page, totalPages: pag.total_pages ?? pag.totalPages } }));
      }
    } else {
      setTabData((p) => ({ ...p, [tab]: null }));
    }
  }, [traderId, depositFilter, p2pAdsFilter]);

  const hasTrader = !!trader;
  useEffect(() => {
    if (!hasTrader) return;
    if (kycOnlyMode && activeTab === "kyc" && tabData.kyc) return;
    if (
      ![
        "wallet",
        "deposit",
        "kyc",
        "internalWallet",
        "spotHistory",
        "spotOpen",
        "swapping",
        "staking",
        "launchpad",
        "support",
        "referral",
        "p2p",
        "p2pAds",
        "p2pPaymentMethods",
        "futureOpen",
        "futurePositions",
        "futureHistory",
      ].includes(activeTab)
    ) {
      return;
    }
    fetchTab(activeTab);
  }, [hasTrader, activeTab, fetchTab, kycOnlyMode, tabData.kyc]);

  const handleTabPageChange = (tab, newPage) => {
    fetchTab(tab, newPage);
    setTabPagination((p) => ({ ...p, [tab]: { ...(p[tab] || {}), page: newPage } }));
  };

  const handleApproveKyc = async (docType) => {
    if (!traderId || kycActionLoading) return;
    setKycApproveModal(null);
    setKycActionLoading(docType);
    const res = docType === "kyc"
      ? await AdminService.approveKyc(traderId)
      : await AdminService.approveKycDocument(traderId, docType);
    setKycActionLoading(null);
    if (res?.success) {
      fetchTab("kyc");
      fetchDetail();
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!traderId || statusUpdating) return;
    setStatusUpdating(true);
    const res = await AdminService.updateUserStatus(traderId, newStatus);
    setStatusUpdating(false);
    if (res?.success) {
      fetchDetail();
    }
  };

  /** PUT /v1/admin/users/unblock — status "Active" (unblock) or "Blocked" (block). */
  const handlePutAccountBlockStatus = async (status) => {
    if (!traderId || !trader || statusUpdating) return;
    if (status !== "Active" && status !== "Blocked") return;
    setStatusUpdating(true);
    const res = await AdminService.updateUserAccountBlockStatus(traderId, status);
    setStatusUpdating(false);
    if (res?.success) {
      fetchDetail();
    }
  };

  const handleRejectKyc = async (docType, rejectReason, adminNotes = "") => {
    if (!traderId || kycActionLoading || !rejectReason?.trim()) return;
    setKycActionLoading(docType);
    setKycRejectModal(null);
    const res = docType === "kyc"
      ? await AdminService.rejectKyc(traderId, rejectReason.trim(), adminNotes.trim())
      : await AdminService.rejectKycDocument(traderId, docType, rejectReason.trim(), adminNotes.trim());
    setKycActionLoading(null);
    if (res?.success) {
      fetchTab("kyc");
      fetchDetail();
    }
  };

  if (loading) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <div className="dashboard-body">
          <div className="trader-detail-loading">
            <i className="fas fa-spinner fa-spin" /> Loading user details…
          </div>
        </div>
      </main>
    );
  }

  if (error || !trader) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <div className="dashboard-body">
          <p className="trader-detail-notfound">{error || "Trader not found."}</p>
          <Link to={hasPermission(PERMISSION_KEYS.USERS) ? "/dashboard/tradelist" : getDefaultDashboardRoute()}>
            Back to List
          </Link>
        </div>
      </main>
    );
  }

  const fName = trader.firstName ?? "";
  const lName = trader.lastName ?? "";
  const isActive = trader.status === 1 || trader.status === "Active";
  const statusLabel = typeof trader.status === "string" ? trader.status : (STATUS_LABELS[trader.status] ?? (trader.status === 1 ? "Active" : "Inactive"));
  const accountStatusRaw = profile?.account_status ?? trader?.account_status;
  const showAccountBlockBtn = isAccountStatusActive(accountStatusRaw);
  const showAccountUnblockBtn = isAccountStatusBlocked(accountStatusRaw);

  const renderEmpty = (msg = "No data") => (
    <div className="trader-tab-empty"><i className="fas fa-inbox" /> {msg}</div>
  );

  const renderTable = (data, cols, emptyMsg) => {
    const rows = Array.isArray(data) ? data : [];
    if (rows.length === 0) return renderEmpty(emptyMsg);
    return (
      <div className="detail-table-wrap">
        <table className="detail-table">
          <thead><tr>{cols.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row._id || i}>
                {cols.map((c) => (
                  <td key={c.key}>{typeof c.render === "function" ? c.render(row) : (row[c.key] ?? "—")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTabContent = () => {
    const pag = tabPagination[activeTab];
    const data = tabData[activeTab];
    const isTabLoading = tabLoading[activeTab];

    if (isTabLoading && (!data || (Array.isArray(data) && data.length === 0))) {
      return <div className="trader-tab-loading"><i className="fas fa-spinner fa-spin" /> Loading…</div>;
    }

    switch (activeTab) {
      case "user": {
        const ver = profile?.verification || profile || {};
        const secMethods = ver.security_methods ?? profile?.security_methods ?? {};
        const loginSec = profile?.login_security ?? {};
        const byWallet = portfolio?.by_wallet_type ?? {};
        const emailHistory = Array.isArray(profile?.email_change_history) ? profile.email_change_history : (Array.isArray(profile?.previous_emails) ? profile.previous_emails : []);
        const mobileHistory = Array.isArray(profile?.mobile_change_history) ? profile.mobile_change_history : (Array.isArray(profile?.previous_phones) ? profile.previous_phones : []);
        const failedAttempts = loginSec.failed_login_attempts ?? profile?.failed_login_attempts ?? 0;
        const accountLockedUntil = loginSec.account_locked_until ?? profile?.account_locked_until ?? null;
        const pendingEmail = profile?.pending_email_change ?? null;
        const pendingMobile = profile?.pending_mobile_change ?? null;

        return (
          <div className="trader-tab-card user-details-card">
            <h3 className="trader-tab-card__title"><i className="fas fa-user" /> User Details</h3>
            <div className="user-details-grid">
              <section className="user-details-group">
                <h4 className="user-details-group__title"><i className="fas fa-id-card" /> Basic Information</h4>
                <div className="user-details-list">
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-user" /> User ID</span><span className="user-detail-item__value user-detail-mono">{trader._id}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-signature" /> Full Name</span><span className="user-detail-item__value">{fName} {lName}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-envelope" /> Email</span><span className="user-detail-item__value">{trader.emailId || "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-phone" /> Mobile</span><span className="user-detail-item__value">{trader.mobileNumber || "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-globe" /> Registered by</span><span className="user-detail-item__value">{profile?.registeredBy || "—"}</span></div>
                </div>
              </section>
              <section className="user-details-group">
                <h4 className="user-details-group__title"><i className="fas fa-wallet" /> Account</h4>
                <div className="user-details-list">
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-fingerprint" /> UUID</span><span className="user-detail-item__value user-detail-mono">{trader.uuid || "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-gift" /> Referral</span><span className="user-detail-item__value user-detail-mono">{trader.referral_code || "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-toggle-on" /> Status</span><span className="user-detail-item__value"><span className={`badge badge--${isActive ? "active" : "inactive"}`}>{statusLabel}</span></span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-id-card" /> KYC</span><span className="user-detail-item__value"><span className={`badge badge--kyc badge--kyc-${trader.kycVerified}`}>{KYC_LABELS[trader.kycVerified] ?? "—"}</span></span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-calendar-plus" /> Joined</span><span className="user-detail-item__value">{formatDate(trader.createdAt)}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label"><i className="fas fa-coins" /> Currency</span><span className="user-detail-item__value">{profile?.currency_prefrence ?? profile?.currency_preference ?? "—"}</span></div>
                </div>
              </section>
              <section className="user-details-group">
                <h4 className="user-details-group__title"><i className="fas fa-shield-alt" /> Security & Verification</h4>
                <div className="user-details-list">
                  <div className="user-detail-item"><span className="user-detail-item__label">Email verified</span><span className="user-detail-item__value">{(profile?.registeredBy==='email' ?? profile?.registeredBy==='google' ??ver.email_verified ?? secMethods?.email) ? "Yes" : "No"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Phone verified</span><span className="user-detail-item__value">{(profile?.registeredBy==='phone' ?? profile?.phone_verified ?? ver.phone_verified ?? secMethods?.mobile) ? "Yes" : "No"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">2FA / TOTP</span><span className="user-detail-item__value">{(secMethods.totp ?? ver.two_fa_enabled ?? profile?.security_method?.totp) ? "Enabled" : "Disabled"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Passkey</span><span className="user-detail-item__value">{(secMethods.passkey ?? profile?.security_method?.passkey) ? "Enabled" : "Disabled"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Last login</span><span className="user-detail-item__value">{formatDate(profile?.last_login_at ?? profile?.security_timestamps?.last_login_at)}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Last login IP</span><span className="user-detail-item__value user-detail-mono">{profile?.last_login_ip ?? profile?.security_timestamps?.last_login_ip ?? "—"}</span></div>
                  {(profile?.security_restrictions?.withdrawal_blocked_until ?? profile?.security_restrictions?.p2p_blocked_until) && (
                    <>
                      {profile?.security_restrictions?.withdrawal_blocked_until && (
                        <div className="user-detail-item"><span className="user-detail-item__label">Withdrawal blocked until</span><span className="user-detail-item__value user-detail-mono">{formatDate(profile.security_restrictions.withdrawal_blocked_until)}</span></div>
                      )}
                      {profile?.security_restrictions?.p2p_blocked_until && (
                        <div className="user-detail-item"><span className="user-detail-item__label">P2P blocked until</span><span className="user-detail-item__value user-detail-mono">{formatDate(profile.security_restrictions.p2p_blocked_until)}</span></div>
                      )}
                    </>
                  )}
                </div>
              </section>
              <section className="user-details-group">
                <h4 className="user-details-group__title"><i className="fas fa-lock" /> Login Security</h4>
                <div className="user-details-list">
                  <div className="user-detail-item"><span className="user-detail-item__label">Failed login attempts</span><span className="user-detail-item__value">{failedAttempts}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Last failed login</span><span className="user-detail-item__value">{formatDate(loginSec.last_failed_login ?? profile?.last_failed_login)}</span></div>
                  {accountLockedUntil && (
                    <div className="user-detail-item"><span className="user-detail-item__label">Account locked until</span><span className="user-detail-item__value user-detail-mono user-detail-alert">{formatDate(accountLockedUntil)}</span></div>
                  )}
                </div>
              </section>
              <section className="user-details-group">
                <h4 className="user-details-group__title"><i className="fas fa-coins" /> Portfolio (USDT)</h4>
                <div className="user-details-list">
                  <div className="user-detail-item"><span className="user-detail-item__label">Total</span><span className="user-detail-item__value">{formatNum(portfolio?.total_usdt)}</span></div>
                  {Object.entries(byWallet).map(([k, v]) => (
                    <div key={k} className="user-detail-item">
                      <span className="user-detail-item__label">
                        {formatWalletType(k)}
                        {isStakingWallet(k) ? " (purchase only)" : ""}
                      </span>
                      <span className="user-detail-item__value">{formatNum(v)}</span>
                    </div>
                  ))}
                </div>
              </section>
              {(pendingEmail ?? pendingMobile) && (
                <section className="user-details-group user-details-group--full">
                  <h4 className="user-details-group__title"><i className="fas fa-clock" /> Pending Changes</h4>
                  <div className="user-details-list">
                    {pendingEmail && (
                      <div className="user-detail-item"><span className="user-detail-item__label">Pending email</span><span className="user-detail-item__value">{pendingEmail.new_email_masked ?? "—"} · Expires {formatDate(pendingEmail.expires_at)}</span></div>
                    )}
                    {pendingMobile && (
                      <div className="user-detail-item"><span className="user-detail-item__label">Pending mobile</span><span className="user-detail-item__value">+{pendingMobile.new_country_code ?? ""} · Expires {formatDate(pendingMobile.expires_at)}</span></div>
                    )}
                  </div>
                </section>
              )}
              {emailHistory.length > 0 && (
                <section className="user-details-group user-details-group--full">
                  <h4 className="user-details-group__title"><i className="fas fa-envelope" /> Email Change History</h4>
                  <div className="detail-table-wrap">
                    <table className="detail-table detail-table--compact">
                      <thead><tr><th>Email</th><th>Changed at</th></tr></thead>
                      <tbody>
                        {emailHistory.slice(0, 10).map((item, i) => (
                          <tr key={item._id ?? i}><td>{item.email ?? "—"}</td><td>{formatDate(item.changed_at)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {emailHistory.length > 10 && <p className="user-detail-meta">Showing latest 10 of {emailHistory.length}</p>}
                </section>
              )}
              {mobileHistory.length > 0 && (
                <section className="user-details-group user-details-group--full">
                  <h4 className="user-details-group__title"><i className="fas fa-phone" /> Mobile Change History</h4>
                  <div className="detail-table-wrap">
                    <table className="detail-table detail-table--compact">
                      <thead><tr><th>Phone</th><th>Country</th><th>Changed at</th></tr></thead>
                      <tbody>
                        {mobileHistory.slice(0, 10).map((item, i) => (
                          <tr key={item._id ?? i}>
                            <td>{item.phone ?? "—"}</td>
                            <td>{item.country_code ?? "—"}</td>
                            <td>{formatDate(item.changed_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {mobileHistory.length > 10 && <p className="user-detail-meta">Showing latest 10 of {mobileHistory.length}</p>}
                </section>
              )}
            </div>
          </div>
        );
      }
      case "wallet": {
        const walletRows = Array.isArray(data) ? data : [];
        const hasStaking = walletRows.some((r) => isStakingWallet(r.wallet_type));
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title">Wallet Details</h3>
            {hasStaking ? (
              <p className="user-detail-meta" style={{ marginTop: 0 }}>
                Staking wallet is shown here for package purchases only. {STAKING_WALLET_HINT}
              </p>
            ) : null}
            {renderTable(walletRows, [
              { key: "short_name", label: "Coin" },
              {
                key: "wallet_type",
                label: "Type",
                render: (r) => formatWalletType(r.wallet_type),
              },
              { key: "balance", label: "Balance", render: (r) => formatNum(r.balance) },
              { key: "locked_balance", label: "Locked", render: (r) => formatNum(r.locked_balance) },
              { key: "bonus", label: "Bonus", render: (r) => formatNum(r.bonus) },
              { key: "status", label: "Status" },
            ], "No wallets")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("wallet", p)} />
          </div>
        );
      }
      case "deposit": {
        const depositRows = Array.isArray(data) ? data : (data?.data ?? data?.transactions ?? []);
        const filteredDeposit = depositFilter && depositFilter !== "all"
          ? depositRows.filter((r) => String(r.transaction_type ?? r.type ?? "").toLowerCase() === depositFilter)
          : depositRows;
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title">Deposit / Withdrawal</h3>
            <div className="trader-tab-filters">
              <label className="trader-tab-filter__label">Filter:</label>
              <select
                className="trader-tab-filter__select"
                value={depositFilter}
                onChange={(e) => { setDepositFilter(e.target.value); setTabPagination((p) => ({ ...p, deposit: { ...(p.deposit || {}), page: 1 } })); }}
              >
                <option value="all">All</option>
                <option value="deposit">Deposit</option>
                <option value="withdrawal">Withdrawal</option>
              </select>
            </div>
            {renderTable(filteredDeposit, [
              { key: "transaction_type", label: "Type" },
              { key: "short_name", label: "Currency" },
              { key: "chain", label: "Chain", render: (r) => r.chain || r.network || "—" },
              { key: "amount", label: "Amount", render: (r) => formatNum(r.amount) },
              { key: "transaction_hash", label: "Tx Hash", render: (r) => r.transaction_hash || r.to_address || "—" },
              { key: "status", label: "Status" },
              { key: "createdAt", label: "Date", render: (r) => formatDate(r.createdAt) },
            ], "No transactions")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("deposit", p)} />
          </div>
        );
      }
      case "kyc": {
        const kyc = data && !Array.isArray(data) ? data : (Array.isArray(data) && data[0]) || null;
        if (!kyc) return renderEmpty("No KYC submitted");
        const idDoc = kyc.id_document ?? {};
        const taxDoc = kyc.tax_document ?? {};
        const selfie = kyc.selfie ?? {};
        const addr = kyc.address ?? {};
        const finalVer = kyc.final_verification ?? {};
        const auditLog = Array.isArray(kyc.audit_log) ? kyc.audit_log : [];
        const dob = kyc.date_of_birth ? (kyc.date_of_birth.includes("T") ? formatDate(kyc.date_of_birth).split(",")[0] : kyc.date_of_birth) : "—";
        const imgUrl = (path) => (path && path.startsWith("http") ? path : path ? `${imageUrl}/${path}` : null);
        const isPending = (s) => !s || String(s).toLowerCase() === "pending" || String(s).toLowerCase() === "submitted";

        return (
          <div className="trader-tab-card kyc-details-card">
            <h3 className="trader-tab-card__title"><i className="fas fa-id-card" /> KYC Details</h3>
            <div className="kyc-status-banner">
              <span className={` badge badge--kyc badge--kyc-${(kyc.overall_status ?? "").toLowerCase()}`}>
                {kyc.overall_status ?? KYC_LABELS[trader.kycVerified] ?? "—"}
              </span>
              {isPending(kyc.overall_status) && (
                <div className="kyc-status-banner__actions">
                  <button type="button" className="btn btn-primary btn-sm" disabled={kycActionLoading === "kyc"} onClick={() => setKycApproveModal({ docType: "kyc", docLabel: "Complete KYC" })}>
                    {kycActionLoading === "kyc" ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />} Approve Complete KYC
                  </button>
                  <button type="button" className="btn btn-danger btn-sm" disabled={kycActionLoading === "kyc"} onClick={() => setKycRejectModal({ docType: "kyc", docLabel: "Complete KYC" })}>
                    <i className="fas fa-times" /> Reject Complete KYC
                  </button>
                </div>
              )}
            </div>
            <div className="user-details-grid">
              <section className="user-details-group">
                <h4 className="user-details-group__title">Personal</h4>
                <div className="user-details-list">
                  <div className="user-detail-item"><span className="user-detail-item__label">Name</span><span className="user-detail-item__value">{[kyc.first_name, kyc.last_name].filter(Boolean).join(" ") || "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">DOB</span><span className="user-detail-item__value">{dob}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Gender</span><span className="user-detail-item__value">{kyc.gender ?? "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Nationality</span><span className="user-detail-item__value">{kyc.nationality ?? addr.country_name ?? "—"}</span></div>
                </div>
              </section>
              <section className="user-details-group kyc-doc-section">
                <h4 className="user-details-group__title">ID Document</h4>
                <div className="user-details-list">
                  <div className="user-detail-item"><span className="user-detail-item__label">Type</span><span className="user-detail-item__value">{idDoc.type ?? "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Number</span><span className="user-detail-item__value user-detail-mono">{idDoc.number_normalized ?? "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Status</span><span className="user-detail-item__value"><span className={`badge badge--kyc-${((idDoc.status ?? "") || "pending").toLowerCase()}`}>{idDoc.status ?? "—"}</span></span></div>
                </div>
                <div className="kyc-doc-images">
                  <KYCImageThumb src={imgUrl(idDoc.front_image_url)} label="Front" onZoom={setKycZoomedImage} />
                  <KYCImageThumb src={imgUrl(idDoc.back_image_url)} label="Back" onZoom={setKycZoomedImage} />
                </div>
                {isPending(idDoc.status) && (
                  <div className="kyc-doc-actions">
                    <button type="button" className="btn btn-primary btn-sm" disabled={kycActionLoading === "id_document"} onClick={() => setKycApproveModal({ docType: "id_document", docLabel: "ID Document" })}>
                      {kycActionLoading === "id_document" ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />} Approve
                    </button>
                    <button type="button" className="btn btn-danger btn-sm" disabled={kycActionLoading === "id_document"} onClick={() => setKycRejectModal({ docType: "id_document", docLabel: "ID Document" })}>
                      <i className="fas fa-times" /> Reject
                    </button>
                  </div>
                )}
              </section>
              <section className="user-details-group kyc-doc-section">
                <h4 className="user-details-group__title">Selfie</h4>
                <div className="user-details-list">
                  <div className="user-detail-item"><span className="user-detail-item__label">Status</span><span className="user-detail-item__value"><span className={`badge badge--kyc-${((selfie.status ?? "") || "pending").toLowerCase()}`}>{selfie.status ?? "—"}</span></span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Captured</span><span className="user-detail-item__value">{formatDate(selfie.captured_at)}</span></div>
                </div>
                <div className="kyc-doc-images">
                  <KYCImageThumb src={imgUrl(selfie?.url)} label="Selfie" onZoom={setKycZoomedImage} />
                </div>
                {selfie?.url && isPending(selfie.status) && (
                  <div className="kyc-doc-actions">
                    <button type="button" className="btn btn-primary btn-sm" disabled={kycActionLoading === "selfie"} onClick={() => setKycApproveModal({ docType: "selfie", docLabel: "Selfie" })}>
                      {kycActionLoading === "selfie" ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />} Approve
                    </button>
                    <button type="button" className="btn btn-danger btn-sm" disabled={kycActionLoading === "selfie"} onClick={() => setKycRejectModal({ docType: "selfie", docLabel: "Selfie" })}>
                      <i className="fas fa-times" /> Reject
                    </button>
                  </div>
                )}
              </section>
              {(taxDoc?.type ?? taxDoc?.number_normalized ?? taxDoc?.file_url) && (
              <section className="user-details-group kyc-doc-section">
                <h4 className="user-details-group__title">Tax Document</h4>
                <div className="user-details-list">
                  <div className="user-detail-item"><span className="user-detail-item__label">Type</span><span className="user-detail-item__value">{taxDoc.type ?? "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Number</span><span className="user-detail-item__value user-detail-mono">{taxDoc.number_normalized ?? "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Status</span><span className="user-detail-item__value"><span className={`badge badge--kyc-${((taxDoc.status ?? "") || "pending").toLowerCase()}`}>{taxDoc.status ?? "—"}</span></span></div>
                </div>
                <div className="kyc-doc-images">
                  <KYCImageThumb src={imgUrl(taxDoc.file_url)} label="Tax Document" onZoom={setKycZoomedImage} />
                </div>
                {taxDoc?.file_url && isPending(taxDoc.status) && (
                  <div className="kyc-doc-actions">
                    <button type="button" className="btn btn-primary btn-sm" disabled={kycActionLoading === "tax_document"} onClick={() => setKycApproveModal({ docType: "tax_document", docLabel: "Tax Document" })}>
                      {kycActionLoading === "tax_document" ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />} Approve
                    </button>
                    <button type="button" className="btn btn-danger btn-sm" disabled={kycActionLoading === "tax_document"} onClick={() => setKycRejectModal({ docType: "tax_document", docLabel: "Tax Document" })}>
                      <i className="fas fa-times" /> Reject
                    </button>
                  </div>
                )}
              </section>
              )}
              <section className="user-details-group">
                <h4 className="user-details-group__title">Address</h4>
                <div className="user-details-list">
                  <div className="user-detail-item"><span className="user-detail-item__label">Line 1</span><span className="user-detail-item__value">{addr.line1 ?? "—"}</span></div>
                  {addr.line2 && <div className="user-detail-item"><span className="user-detail-item__label">Line 2</span><span className="user-detail-item__value">{addr.line2}</span></div>}
                  <div className="user-detail-item"><span className="user-detail-item__label">City</span><span className="user-detail-item__value">{addr.city ?? "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">State</span><span className="user-detail-item__value">{addr.state ?? "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Postal code</span><span className="user-detail-item__value">{addr.postal_code ?? "—"}</span></div>
                  <div className="user-detail-item"><span className="user-detail-item__label">Country</span><span className="user-detail-item__value">{addr.country_name ?? addr.country_code ?? "—"}</span></div>
                </div>
              </section>
              {finalVer && (finalVer.method ?? finalVer.verified_at) && (
                <section className="user-details-group">
                  <h4 className="user-details-group__title">Final Verification</h4>
                  <div className="user-details-list">
                    <div className="user-detail-item"><span className="user-detail-item__label">Method</span><span className="user-detail-item__value">{finalVer.method ?? "—"}</span></div>
                    <div className="user-detail-item"><span className="user-detail-item__label">Verified</span><span className="user-detail-item__value">{finalVer.verified ? "Yes" : "No"}</span></div>
                    <div className="user-detail-item"><span className="user-detail-item__label">At</span><span className="user-detail-item__value">{formatDate(finalVer.verified_at)}</span></div>
                  </div>
                </section>
              )}
              {auditLog.length > 0 && (
                <section className="user-details-group user-details-group--full">
                  <h4 className="user-details-group__title">Audit Log</h4>
                  <div className="detail-table-wrap">
                    <table className="detail-table detail-table--compact">
                      <thead><tr><th>Action</th><th>Note</th><th>Previous Files</th><th>Date</th></tr></thead>
                      <tbody>
                        {[...(auditLog ?? [])].reverse().map((entry, i) => {
                          const prev = entry.previous_file_paths ?? {};
                          const hasPrev = prev.id_document || prev.tax_document || prev.selfie;
                          return (
                            <tr key={entry._id ?? i}>
                              <td>{entry.action ?? "—"}</td>
                              <td>{entry.note ?? "—"}</td>
                              <td>
                                {hasPrev ? (
                                  <div className="kyc-audit-prev">
                                    {prev.id_document && (prev.id_document.front_image_url || prev.id_document.back_image_url) && (
                                      <div className="kyc-audit-prev__doc">
                                        <span className="kyc-audit-prev__label"> ID:</span>
                                        <KYCImageThumb src={imgUrl(prev.id_document.front_image_url)} label="Prev front" onZoom={setKycZoomedImage} />
                                        <KYCImageThumb src={imgUrl(prev.id_document.back_image_url)} label="Prev back" onZoom={setKycZoomedImage} />
                                      </div>
                                    )}
                                    {prev.tax_document?.file_url && (
                                      <div className="kyc-audit-prev__doc">
                                        <span className="kyc-audit-prev__label"> Tax:</span>
                                        <KYCImageThumb src={imgUrl(prev.tax_document.file_url)} label="Prev tax" onZoom={setKycZoomedImage} />
                                      </div>
                                    )}
                                    {prev.selfie?.url && (
                                      <div className="kyc-audit-prev__doc">
                                        <span className="kyc-audit-prev__label"> Selfie:</span>
                                        <KYCImageThumb src={imgUrl(prev.selfie.url)} label="Prev selfie" onZoom={setKycZoomedImage} />
                                      </div>
                                    )}
                                  </div>
                                ) : "—"}
                              </td>
                              <td>{formatDate(entry.at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </div>
            <p className="kyc-timestamps">
              Submitted: {formatDate(kyc.submitted_at ?? kyc.createdAt)} · Updated: {formatDate(kyc.updatedAt)}
            </p>
          </div>
        );
      }
      case "spotHistory":
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title">Spot Trades</h3>
            {renderTable(data, [
              { key: "side", label: "Side", render: (r) => <span className={r.side === "BUY" ? "text-buy" : "text-sell"}>{r.side}</span> },
              { key: "quantity", label: "Qty", render: (r) => formatNum(r.quantity) },
              { key: "price", label: "Price", render: (r) => formatNum(r.price) },
              { key: "fee", label: "Fee", render: (r) => formatNum(r.fee) },
              { key: "order_type", label: "Type" },
              { key: "createdAt", label: "Time", render: (r) => formatDate(r.createdAt) },
            ], "No spot trades")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("spotHistory", p)} />
          </div>
        );
      case "spotOpen":
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title">Spot Orders</h3>
            {renderTable(data, [
              { key: "orderId", label: "Order" },
              { key: "side", label: "Side" },
              { key: "ask_currency", label: "Ask" },
              { key: "pay_currency", label: "Pay" },
              { key: "price", label: "Price", render: (r) => formatNum(r.price) },
              { key: "quantity", label: "Qty", render: (r) => formatNum(r.quantity) },
              { key: "filled", label: "Filled", render: (r) => formatNum(r.filled) },
              { key: "total_fee", label: "Fee", render: (r) => `${formatNum(r.total_fee)} ${r.side==="BUY" ? r.ask_currency : r.pay_currency}` },
              { key: "status", label: "Status" },
              { key: "createdAt", label: "Date", render: (r) => formatDate(r.createdAt) },
            ], "No open orders")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("spotOpen", p)} />
          </div>
        );
      case "futureOpen": {
        const futureOrdersData = Array.isArray(data) ? data : (data?.data ?? []);
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title">Futures Orders</h3>
            {renderTable(futureOrdersData, [
              { key: "orderId", label: "Order ID", render: (r) => <span className="user-detail-mono">{r.orderId ?? "—"}</span> },
              { key: "pair_id", label: "Pair", render: (r) => r.pair_id?.short_name || r.pair_id?.name || r.symbol || "—" },
              { key: "side", label: "Side", render: (r) => <span className={r.side === "LONG" ? "text-buy" : "text-sell"}>{r.side ?? "—"}</span> },
              { key: "quantity", label: "Qty", render: (r) => formatNum(r.quantity) },
              { key: "price", label: "Price", render: (r) => (r.price != null ? formatNum(r.price) : (r.avgFillPrice != null ? formatNum(r.avgFillPrice) : "Market")) },
              { key: "type", label: "Type" },
              { key: "filledQty", label: "Filled", render: (r) => formatNum(r.filledQty) },
              { key: "feePaid", label: "Fee", render: (r) => `${formatNum(r.feePaid)} ${r.marginAsset} `},
              { key: "leverage", label: "Lev" },
              { key: "status", label: "Status", render: (r) => <span className={`badge badge--${(r.status ?? "").toLowerCase()}`}>{r.status ?? "—"}</span> },
              { key: "createdAt", label: "Date", render: (r) => formatDate(r.createdAt) },
            ], "No futures orders")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("futureOpen", p)} />
          </div>
        );
      }
      case "futurePositions": {
        const futurePosData = Array.isArray(data) ? data : (data?.data ?? []);
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title">Futures Positions</h3>
            {renderTable(futurePosData, [
              { key: "pair_id", label: "Pair", render: (r) => r.pair_id?.short_name || r.pair_id?.name || r.symbol || "—" },
              { key: "side", label: "Side", render: (r) => <span className={r.side === "LONG" ? "text-buy" : "text-sell"}>{r.side ?? "—"}</span> },
              { key: "quantity", label: "Size", render: (r) => formatNum(r.quantity || (r.side === "LONG" ? r.totalLongQty : r.totalShortQty)) },
              { key: "entryPrice", label: "Entry", render: (r) => formatNum(r.entryPrice ?? r.entry_price) },
              { key: "exit_price", label: "Exit", render: (r) => formatNum(r.exit_price) },
              { key: "leverage", label: "Lev" },
              { key: "unrealizedPnl", label: "Unrealized PnL", render: (r) => <span className={Number(r.unrealizedPnl) >= 0 ? "text-buy" : "text-sell"}>{formatNum(r.unrealizedPnl)}</span> },
              { key: "realizedPnl", label: "Realized PnL", render: (r) => <span className={Number(r.realizedPnl) >= 0 ? "text-buy" : "text-sell"}>{formatNum(r.realizedPnl)}</span> },
              { key: "liquidationPrice", label: "Liq. Price", render: (r) => formatNum(r.liquidationPrice) },
              { key: "status", label: "Status", render: (r) => <span className={`badge badge--${(r.status ?? "").toLowerCase()}`}>{r.status ?? "—"}</span> },
              { key: "createdAt", label: "Opened", render: (r) => formatDate(r.createdAt) },
              { key: "closed_at", label: "Closed", render: (r) => formatDate(r.closed_at) },
            ], "No futures positions")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("futurePositions", p)} />
          </div>
        );
      }
      case "futureHistory": {
        const futureHistoryData = Array.isArray(data) ? data : (data?.data ?? []);
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title">Futures Trade History</h3>
            {renderTable(futureHistoryData, [
              { key: "order_id", label: "Order ID", render: (r) => <span className="user-detail-mono">{r.order_id ?? "—"}</span> },
              { key: "pair_id", label: "Pair", render: (r) => r.pair_id?.short_name || r.pair_id?.name || r.symbol || "—" },
              { key: "side", label: "Side", render: (r) => <span className={r.side === "LONG" ? "text-buy" : "text-sell"}>{r.side ?? "—"}</span> },
              { key: "quantity", label: "Size", render: (r) => formatNum(r.quantity) },
              { key: "price", label: "Price", render: (r) => formatNum(r.price) },
              { key: "fee", label: "Fee", render: (r) => formatNum(r.fee) },
              { key: "role", label: "Role" },
              // { key: "pnl", label: "PnL", render: (r) => <span className={Number(r.pnl) >= 0 ? "text-buy" : "text-sell"}>{formatNum(r.pnl)}</span> },
              { key: "createdAt", label: "Date", render: (r) => formatDate(r.createdAt) },
            ], "No futures trades")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("futureHistory", p)} />
          </div>
        );
      }
      case "staking": {
        const stakingData = Array.isArray(data) ? data : (data?.data ?? []);
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title"><i className="fas fa-coins" /> Staking / Earning Plans</h3>
            {renderTable(stakingData, [
              { key: "currency", label: "Currency", render: (r) => r.currency_fullname ? `${r.currency} (${r.currency_fullname})` : (r.currency ?? "—") },
              { key: "invested_amount", label: "Invested", render: (r) => formatNum(extractDecimal(r.invested_amount)) },
              { key: "duration_days", label: "Duration", render: (r) => `${r.duration_days ?? r.plan_id?.duration_days ?? "—"} days` },
              { key: "expected_return", label: "Expected Return", render: (r) => formatNum(extractDecimal(r.expected_return)) },
              { key: "start_date", label: "Start Date", render: (r) => formatDate(r.start_date) },
              { key: "end_date", label: "End Date", render: (r) => formatDate(r.end_date) },
              { key: "status", label: "Status" },
              {
                key: "notes",
                label: "Notes",
                render: (r) => {
                  const n = r.notes;
                  if (n == null || String(n).trim() === "") return "—";
                  const s = String(n).trim();
                  const short = s.length > 60 ? `${s.slice(0, 60)}…` : s;
                  return <span title={s}>{short}</span>;
                },
              },
              {
                key: "wallet_type",
                label: "Wallet",
                render: (r) => formatWalletType(r.wallet_type),
              },
              {
                key: "credited_wallet_type",
                label: "Credit To",
                render: (r) => formatWalletType(r.credited_wallet_type),
              },
              { key: "createdAt", label: "Created", render: (r) => formatDate(r.createdAt) },
            ], "No earning plans")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("staking", p)} />
          </div>
        );
      }
      case "launchpad":
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title">Launchpad</h3>
            {renderTable(data, [
              { key: "launchpadId", label: "Token", render: (r) => r.launchpadId?.tokenName || r.launchpadId?.tokenSymbol || "—" },
              { key: "amountInvested", label: "Invested", render: (r) => formatNum(r.amountInvested) },
              { key: "tokensReceived", label: "Tokens", render: (r) => formatNum(r.tokensReceived) },
              { key: "purchaseTime", label: "Date", render: (r) => formatDate(r.purchaseTime ?? r.createdAt) },
            ], "No launchpad purchases")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("launchpad", p)} />
          </div>
        );
      case "internalWallet":
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title">Internal Transfers</h3>
            <InternalTransferSettingsPanel userId={traderId} />
            <h4 className="trader-tab-card__subtitle">Transfer history</h4>
            {renderTable(data, [
              { key: "short_name", label: "Currency" },
              { key: "amount", label: "Amount", render: (r) => formatNum(r.amount) },
              { key: "from_wallet", label: "From" },
              { key: "to_wallet", label: "To" },
              { key: "status", label: "Status" },
              { key: "createdAt", label: "Date", render: (r) => formatDate(r.createdAt) },
            ], "No internal transfers")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("internalWallet", p)} />
          </div>
        );
      case "swapping": {
        const swapData = Array.isArray(data) ? data : (data?.data ?? []);
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title"><i className="fas fa-sync-alt" /> Swap History</h3>
            {renderTable(swapData, [
              { key: "from_to", label: "Pair", render: (r) => `${r.from ?? "—"} → ${r.to ?? "—"}` },
              { key: "pay_amount", label: "Pay", render: (r) => formatNum(r.pay_amount) },
              { key: "conversion_rate", label: "Rate", render: (r) => formatNum(r.conversion_rate) },
              { key: "get_amount", label: "Get", render: (r) => formatNum(r.get_amount) },
              { key: "fee", label: "Fee", render: (r) => formatNum(r.fee) },
              { key: "status", label: "Status" },
              { key: "createdAt", label: "Date", render: (r) => formatDate(r.createdAt) },
            ], "No swap history")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("swapping", p)} />
          </div>
        );
      }
      case "p2p": {
        const p2pData = Array.isArray(data) ? data : (data?.data ?? []);
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title"><i className="fas fa-exchange-alt" /> P2P Orders</h3>
            {renderTable(p2pData, [
              { key: "role", label: "Role", render: (r) => <span className={`badge badge--${(r.role ?? "").toLowerCase()}`}>{r.role ?? "—"}</span> },
              { key: "adUid", label: "Ad ID", render: (r) => <span className="user-detail-mono">{r.adId?.adUid ?? r.adId?._id ?? "—"}</span> },
              { key: "pair", label: "Pair", render: (r) => `${r.adId?.qouteCurrency ?? r.adId?.quoteCurrency ?? "USDT"}/${r.adId?.fiatCurrency ?? r.adId?.fiat ?? "—"}` },
              { key: "price", label: "Price", render: (r) => formatNum(r.price) },
              { key: "cryptoAmount", label: "Crypto", render: (r) => formatNum(r.cryptoAmount ?? r.cryptoSold) },
              { key: "fiatAmount", label: "Fiat Amount", render: (r) => formatNum(r.fiatAmount ?? r.fiat_amount) },
              { key: "p2pFee", label: "Fee", render: (r) => formatNum(r.p2pFee ?? r.p2p_fee) },
              { key: "status", label: "Status", render: (r) => <span className={`badge badge--p2p-${(r.status ?? "").toLowerCase().replace("_", "-")}`}>{r.status ?? "—"}</span> },
              { key: "isDisputed", label: "Dispute", render: (r) => r.isDisputed ? <span className="badge badge--danger">Yes</span> : "No" },
              { key: "createdAt", label: "Created", render: (r) => formatDate(r.createdAt) },
            ], "No P2P orders")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("p2p", p)} />
          </div>
        );
      }
      case "p2pAds": {
        const p2pAdsData = Array.isArray(data) ? data : (data?.data ?? []);
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title"><i className="fas fa-ad" /> P2P Ads</h3>
            <div className="trader-tab-filters">
              <label className="trader-tab-filter__label">Side:</label>
              <select
                className="trader-tab-filter__select"
                value={p2pAdsFilter}
                onChange={(e) => { setP2pAdsFilter(e.target.value); setTabPagination((p) => ({ ...p, p2pAds: { ...(p.p2pAds || {}), page: 1 } })); }}
              >
                <option value="all">All</option>
                <option value="BUY">Buy</option>
                <option value="SELL">Sell</option>
              </select>
            </div>
            {renderTable(p2pAdsData, [
              { key: "adUid", label: "Ad ID", render: (r) => <span className="user-detail-mono">{r.adUid ?? r._id ?? "—"}</span> },
              { key: "side", label: "Side", render: (r) => <span className={`badge badge--${(r.side ?? "").toLowerCase()}`}>{r.side ?? "—"}</span> },
              { key: "pair", label: "Pair", render: (r) => `${r.qouteCurrency ?? r.quoteCurrency ?? "USDT"}/${r.fiatCurrency ?? "—"}` },
              { key: "fixedPrice", label: "Price", render: (r) => formatNum(r.fixedPrice ?? r.fixed_price) },
              { key: "volume", label: "Volume", render: (r) => formatNum(r.volume) },
              { key: "remainingVolume", label: "Remaining", render: (r) => formatNum(r.remainingVolume ?? r.remaining_volume) },
              { key: "limits", label: "Limit", render: (r) => `${formatNum(r.minLimit ?? r.min_limit)} - ${formatNum(r.maxLimit ?? r.max_limit)}` },
              { key: "status", label: "Status", render: (r) => r.status ?? "—" },
              { key: "isOnline", label: "Online", render: (r) => r.isOnline ? "Yes" : "No" },
              { key: "paymentMethodType", label: "Payment", render: (r) => (Array.isArray(r.paymentMethodType) ? r.paymentMethodType.join(", ") : (r.paymentMethodType ?? "—")) },
              { key: "createdAt", label: "Created", render: (r) => formatDate(r.createdAt) },
            ], "No P2P ads")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("p2pAds", p)} />
          </div>
        );
      }
      case "p2pPaymentMethods": {
        const pmData = Array.isArray(data) ? data : (data?.data ?? []);
        const pmImgUrl = (path) => (path && path.startsWith("http") ? path : path ? `${imageUrl}${path}` : null);
        const renderPmDetails = (r) => {
          if (r.type === "UPI" || r.type === "UPI_APP") {
            return r.upiId ?? r.mobileNumber ?? "—";
          }
          if (r.type === "BANK") {
            return [r.bankName, r.accountNumber, r.ifscCode, r.branchName].filter(Boolean).join(" · ") || "—";
          }
          return r.upiId ?? r.mobileNumber ?? r.accountNumber ?? "—";
        };
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title"><i className="fas fa-credit-card" /> P2P Payment Methods</h3>
            {renderTable(pmData, [
              { key: "name", label: "Name" },
              { key: "type", label: "Type" },
              { key: "details", label: "Details", render: renderPmDetails },
              { key: "qrCode", label: "QR Code", render: (r) => r.qrCode ? <a href={pmImgUrl(r.qrCode)} target="_blank" rel="noopener noreferrer" className="user-detail-link">View</a> : "—" },
            ], "No payment methods")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("p2pPaymentMethods", p)} />
          </div>
        );
      }
      case "support":
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title">Support Tickets</h3>
            {renderTable(data, [
              { key: "ticketId", label: "Ticket" },
              { key: "subject", label: "Subject" },
              { key: "status", label: "Status" },
              { key: "createdAt", label: "Date", render: (r) => formatDate(r.createdAt) },
            ], "No support tickets")}
            <TabPagination pagination={pag} onPageChange={(p) => handleTabPageChange("support", p)} />
          </div>
        );
      case "referral": {
        const refData = data && !Array.isArray(data) ? data : null;
        const referralList = Array.isArray(data)
          ? data
          : (refData?.referrals ?? refData?.referred_users ?? refData?.data ?? (refData ? [refData] : []));
        const refCode = trader?.referral_code ?? refData?.referral_code ?? "—";
        const totalRefer =  (Array.isArray(referralList) ? referralList.length : 0);
        return (
          <div className="trader-tab-card">
            <h3 className="trader-tab-card__title"><i className="fas fa-gift" /> Referral</h3>
            <div className="referral-stats">
              <div className="user-detail-item"><span className="user-detail-item__label">Referral Code</span><span className="user-detail-item__value user-detail-mono">{refCode}</span></div>
              <div className="user-detail-item"><span className="user-detail-item__label">Total Referred</span><span className="user-detail-item__value">{totalRefer}</span></div>
              {refData?.total_earnings != null && <div className="user-detail-item"><span className="user-detail-item__label">Earnings</span><span className="user-detail-item__value">{formatNum(refData.total_earnings)}</span></div>}
            </div>
            {referralList?.length > 0 ? (
              <>
                <h4 className="referral-list-title">Referred Users</h4>
                {renderTable(referralList, [
                  { key: "name", label: "Name", render: (r) => [r.firstName ?? r.first_name, r.lastName ?? r.last_name].filter(Boolean).join(" ").trim() || "—" },
                  { key: "emailId", label: "Email", render: (r) => r.emailId ?? r.email ?? r.email_masked ?? "—" },
                  { key: "referral_code", label: "Referral Code", render: (r) => <span className="user-detail-mono">{r.referral_code ?? "—"}</span> },
                  { key: "uuid", label: "UUID", render: (r) => <span className="user-detail-mono cursor-pointer" onClick={() => navigate(`/dashboard/trader/${r._id}`, { state: { trader: r } })}>{r.uuid ?? "—"}</span> },
                  { key: "status", label: "Status", render: (r) => r.status ?? r.account_status ?? "—" },
                  { key: "total_refer", label: "Their Referrals", render: (r) => r.total_refer ?? r.referral_count ?? "—" },
                  { key: "createdAt", label: "Joined", render: (r) => formatDate(r.createdAt) },
                ], "No referred users")}
              </>
            ) : (
              renderEmpty("No referred users")
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage trader-details-page">
      <section className="dashboard-hero trader-details-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to={getDefaultDashboardRoute()}>Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            {hasPermission(PERMISSION_KEYS.USERS) ? (
              <>
                <Link to="/dashboard/tradelist">Traders List</Link>
                <span style={{ margin: "0 0.35rem" }}>/</span>
              </>
            ) : (
              <>
                <Link to="/dashboard/pendingkyc">KYC Management</Link>
                <span style={{ margin: "0 0.35rem" }}>/</span>
              </>
            )}
            <span>{fName} {lName}</span>
          </p>
          <h1 className="dashboard-hero__title">{fName} {lName}</h1>
          <p className="dashboard-hero__subtitle">{trader.emailId || "—"} · {trader.uuid || "—"}</p>
          <div className="trader-details-hero__badges">
            {!kycOnlyMode && (
              <select
                className={`trader-status-select trader-status-select--${isActive ? "active" : "inactive"}`}
                value={trader.status === 1 || trader.status === "Active" ? "Active" : "Inactive"}
                onChange={(e) => handleUpdateStatus(e.target.value)}
                disabled={statusUpdating}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            )}
            <span className={`badge badge--kyc badge--kyc-${trader.kycVerified}`}>{KYC_LABELS[trader.kycVerified] ?? "—"}</span>
            {!kycOnlyMode && showAccountBlockBtn && (
              <button
                type="button"
                className="btn btn-sm trader-account-block-btn trader-account-block-btn--hero"
                onClick={() => handlePutAccountBlockStatus("Blocked")}
                disabled={statusUpdating}
              >
                {statusUpdating ? <i className="fas fa-spinner fa-spin" aria-hidden /> : <i className="fas fa-ban" aria-hidden />}
                {" "}Block
              </button>
            )}
            {!kycOnlyMode && showAccountUnblockBtn && (
              <button
                type="button"
                className="btn btn-sm trader-account-unblock-btn trader-account-unblock-btn--hero"
                onClick={() => handlePutAccountBlockStatus("Active")}
                disabled={statusUpdating}
              >
                {statusUpdating ? <i className="fas fa-spinner fa-spin" aria-hidden /> : <i className="fas fa-unlock-alt" aria-hidden />}
                {" "}Unblock
              </button>
            )}
          </div>
        </div>
        <div className="trader-details-hero__actions">
          <div className="trader-details-hero__avatar">
            {((fName[0] || "") + (lName[0] || "")).toUpperCase() || "?"}
          </div>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() =>
              navigate(
                hasPermission(PERMISSION_KEYS.USERS)
                  ? "/dashboard/tradelist"
                  : getDefaultDashboardRoute()
              )
            }
          >
            <i className="fas fa-arrow-left" /> Back to List
          </button>
        </div>
      </section>

      <div className="dashboard-body trader-details-body">
        <div className="trader-details-layout">
          <nav className="trader-details-tabs" aria-label="Trader details sections">
            {visibleTabSections.map((section) => (
              <div key={section.label} className="trader-details-tabs-section">
                <div className="trader-details-section-label">{section.label}</div>
                {section.tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`trader-details-tab ${activeTab === tab.key ? "trader-details-tab--active" : ""}`}
                    onClick={() => {
                      setActiveTab(tab.key);
                      scrollTraderContentIntoViewIfMobile();
                    }}
                  >
                    <i className={`fas ${tab.icon}`} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>
          <div ref={traderContentRef} className="trader-details-content">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {kycZoomedImage && (
        <div className="kyc-image-zoom" onClick={() => setKycZoomedImage(null)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Escape" && setKycZoomedImage(null)} aria-label="Close zoom">
          <img src={kycZoomedImage} alt="Document full size" onClick={(e) => e.stopPropagation()} />
          <button type="button" className="kyc-image-zoom__close" onClick={() => setKycZoomedImage(null)} aria-label="Close"><i className="fas fa-times" /></button>
        </div>
      )}

      {kycApproveModal && (
        <ApproveKycModal
          docType={kycApproveModal.docType}
          docLabel={kycApproveModal.docLabel}
          onConfirm={handleApproveKyc}
          onClose={() => setKycApproveModal(null)}
          loading={!!kycActionLoading}
        />
      )}

      {kycRejectModal && (
        <RejectKycDocModal
          docType={kycRejectModal.docType}
          docLabel={kycRejectModal.docLabel}
          onConfirm={handleRejectKyc}
          onClose={() => setKycRejectModal(null)}
          loading={!!kycActionLoading}
        />
      )}
    </main>
  );
};

export default TraderDetailsPage;
